import clsx from 'clsx'
import filter from 'lodash/filter'
import isEmpty from 'lodash/isEmpty'
import React, { FC, Fragment, useContext, useEffect, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import { recoverableCartProducts } from '@Analytics/helpers'
import SvgClubTrendhim from '@Assets/SVG/ClubTrendhim'
import { CartLoading } from '@Components/Cart/Listings/CartLoading'
import { NativeModal, NativeModalBody, NativeModalFooter, NativeModalHeader, useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { PaymentIcons } from '@Components/Payment/PaymentIcons'
import { TRUST_PILOT_WIDGET_TEMPLATE_MICRO_STAR, TrustPilotWidget } from '@Components/TrustPilotWidget'
import { Wordbook } from '@Components/Wordbook'
import { Button } from '@CoreComponents/Buttons/Button'
import { SideCartEmpty } from '@Domains/Cart/Feature/CartV2/Components/SideCartEmpty/SideCartEmpty'
import { CartMilestonesV2 } from '@Domains/Cart/Feature/SideCart/Components/CartMilestones/CartMilestonesV2'
import { CartTaxAndDuties } from '@Domains/Cart/Feature/SideCart/Components/Display/CartTaxAndDuties'
import { SideCartRecommendationsV2 } from '@Domains/Cart/Feature/SideCart/Components/Recommendations/SideCartRecommendationsV2'
import {
  CartMilestoneStepType,
  RecommendationContentFragment,
  useCartQuery,
  useRefreshCartMutation,
  useSetDiscountCodeInCartMutation,
} from '@Generated/graphql'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'

import { CartOverviewV2, GetNumberOfLineItemsInCart } from '../../../CartV2/Components/CartOverview/CartOverview'
import { useDiscountCodes } from '../../../DiscountCodes'
import { FreeShippingCountdown } from '../../../FreeShippingCountdown/FreeShippingCountdown'
import { SideCartCouponCode } from '../CouponCode/SideCartCouponCode'
import styles from './SideCartDisplay.module.scss'
import stylesV2 from './SideCartDisplayV2.module.scss'

export const SideCartDisplay: FC = () => {
  const errorsActionRef = useNativeModalActions()
  const { page } = useContext(MetadataContext)
  const {
    data,
    loading,
  } = useCartQuery({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
    skip: page?.type === 'purchase',
  })
  const [refreshCart, { loading: refreshCartLoading }] = useRefreshCartMutation({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
    variables: {
      fixErrors: true,
    },
  })

  const lineItems = data?.cart?.lineItems
  const totals = data?.cart?.totals
  const isFree = !!data?.cart?.freeShipping?.isFree
  const remaining = data?.cart?.freeShipping?.remaining
  const lineItemsWithErrors = filter(lineItems, item => item?.errors && item?.errors.length !== 0) as typeof lineItems

  const [tracked, setTracked] = useState(false)
  const [setDiscountCodeInCart] = useSetDiscountCodeInCartMutation({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
  })
  const [recommendations, setRecommendations] = useState<NonNullable<RecommendationContentFragment['recommendations']> | undefined>(
    data?.cart?.recommendations || undefined,
  )

  const { storeURL } = useContext(MetadataContext)
  const { t } = useTranslation('cart')

  useEffect(() => {
    const clearItem = () => {
      if (window.localStorage.getItem('triggerSideCart')) {
        window.localStorage.removeItem('triggerSideCart')
      }
    }

    window.addEventListener('pageshow', clearItem)

    return () => {
      window.removeEventListener('pageshow', clearItem)
    }
  }, [])

  useEffect(() => {
    setRecommendations(data?.cart?.recommendations || undefined)
    if (lineItems && lineItems.length > 0 && !tracked) {
      ampli.eCommerceCheckout({
        cof_step_no: 0,
        recoverable_cart_products: recoverableCartProducts(data?.cart),
      })
      setTracked(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems])

  useEffect(() => {
    ampli.promptOpen({ source_id: 'cart_prompt' })
  }, [])

  const { apply } = useDiscountCodes()
  const grandTotal = totals?.find(total => total.id === 'grand_total')
  const otherTotals = totals?.filter(total => total.id !== 'grand_total')

  const handleCheckoutClick = () => {
    if (data?.cart?.valid === false) {
      errorsActionRef.current?.open()
      ampli.promptOpen({ source_id: 'cart_error' })
      return
    }

    window.localStorage.setItem('triggerSideCart', 'true')
    window.location.href = storeURL('onestepcheckout')
  }

  if ((!loading && (!lineItems || lineItems.length === 0))) {
    return (
      <>
        <NativeModalHeader>
          {t('header:CART_SHORT_LABEL')}
        </NativeModalHeader>
        <NativeModalBody>
          <SideCartEmpty />
        </NativeModalBody>
        <NativeModalFooter>
          <Button className={stylesV2.button} fillWidth href={storeURL('')} innerSpacing="medium" theme="black">
            {t('cart:START_YOUR_SHOPPING_HERE')}
          </Button>
        </NativeModalFooter>
      </>
    )
  }

  return (
    <>
      <NativeModalHeader className={stylesV2.modalHeader}>
        {`${t('header:CART_SHORT_LABEL')}(${GetNumberOfLineItemsInCart(data?.cart?.lineItems)})`}
      </NativeModalHeader>
      <CartLoading loading={loading}>
        <NativeModalBody className={stylesV2.modalBody}>
          <CartMilestonesV2 cart={data?.cart} className="fullwidth" />
          {!isEmpty(lineItems) && (
            <>
              <CartOverviewV2
                cart={data?.cart}
                className={stylesV2.cartOverview}
                setMutatedCart={() => {
                }}
                v2
              />
              <div className={stylesV2.cartContent}>

                <FreeShippingCountdown
                  expectedDelivery={data?.cart?.expectedDelivery || ''}
                  isFree={isFree}
                  remaining={remaining || undefined}
                  showFreeShippingProgress={(!data?.cart?.milestones?.steps?.some(step => step.type === CartMilestoneStepType.FreeShipping))}
                  v2
                />
                <div className={clsx(stylesV2.totalsWrapper, 'fullwidth')}>
                  <div className={stylesV2.totals}>

                    {otherTotals?.map(total => (
                      <div key={total.id} className={stylesV2.total}>
                        {total.id === 'cashback'
                          ? (
                            <>
                              <span>
                                <SvgClubTrendhim className={stylesV2.cashbackIcon} />
                              </span>
                              <span>

                                {total.value?.format ? total.value.format : total.text}
                                <button
                                  className={clsx(stylesV2.addRemoveCashbackButton, stylesV2.remove)}
                                  onClick={() => setDiscountCodeInCart({ variables: { discountCode: '-cashback' } })}
                                  type="button"
                                >
                                  {t('common:TRENDHIMCLUB_REMOVE_LABEL')}
                                </button>
                              </span>
                            </>
                          )
                          : (
                            <>
                              <span>
                                {total.title}
                              </span>
                              <span>
                                {total.value?.format ? total.value.format : total.text}
                              </span>
                            </>
                          )}
                      </div>
                    ))}
                    {data?.cart?.club?.isLoggedIn && !otherTotals?.some(a => a.id === 'cashback') && (data?.cart?.club?.balance?.value || 0) > 0 && (
                      <div key="apply_cashback" className={stylesV2.total}>
                        <span>
                          <SvgClubTrendhim className={stylesV2.cashbackIcon} />
                        </span>
                        <span>
                          <button
                            className={stylesV2.addRemoveCashbackButton}
                            onClick={() => setDiscountCodeInCart({ variables: { discountCode: '+cashback' } })}
                            type="button"
                          >
                            {t('common:APPLY')}
                          </button>
                        </span>
                      </div>
                    )}
                    <SideCartCouponCode applyCouponCode={apply} v2 />
                  </div>
                </div>
                <div className={clsx(stylesV2.trustWrapper, 'fullwidth')}>
                  <TrustPilotWidget className={stylesV2.trust} trustPilotWidgetTemplate={TRUST_PILOT_WIDGET_TEMPLATE_MICRO_STAR} />
                </div>
              </div>
              {recommendations && (
                <SideCartRecommendationsV2 recommendations={recommendations} />
              )}
            </>
          )}
        </NativeModalBody>
      </CartLoading>
      <NativeModalFooter className={stylesV2.modalFooter}>
        {grandTotal && (
          <div className={stylesV2.grandTotal}>
            <span className={stylesV2.grandTotalTitle}>{grandTotal.title}</span>
            <span className={stylesV2.grandTotalValue}>
              {grandTotal.value?.format ? grandTotal.value.format : grandTotal.text}
            </span>
          </div>
        )}

        <CartTaxAndDuties className={stylesV2.taxIncl} />

        <Button className={stylesV2.button} fillWidth innerSpacing="medium" onClick={handleCheckoutClick} theme="black">
          {t('CART_GO_TO_CHECKOUT_BUTTON')}
        </Button>

        <PaymentIcons enableSlider />

      </NativeModalFooter>

      {lineItemsWithErrors && lineItemsWithErrors.length !== 0 && (
        <NativeModal
          actionRef={errorsActionRef}
          alignment="right"
          backdrop
          className={styles.cartErrors}
          closeOnBackDrop
          closeOnEscape
        >
          <NativeModalHeader className={styles.error}>
            {t('cart:CART_ERROR_TITLE', { count: lineItemsWithErrors.length })}
          </NativeModalHeader>
          <CartLoading loading={refreshCartLoading}>
            <NativeModalBody>
              <div>
                {lineItemsWithErrors.map(item => (
                  <div key={item.id} className={styles.lineItemError}>
                    {item.product?.name && <p><strong>{item.product.name}</strong></p>}
                    {item.errors?.map(error => (
                      <Fragment key={error.code}>
                        {error.message && (
                          <span className={styles.error}>{error.message}</span>
                        )}
                        {error.message && error.suggestion && ' - '}
                        {error.suggestion && <Wordbook wordbook={error.suggestion} />}
                      </Fragment>
                    ))}
                  </div>
                ))}
              </div>
            </NativeModalBody>
          </CartLoading>
          <NativeModalFooter>
            <Button
              className={stylesV2.button}
              fillWidth
              innerSpacing="medium"
              onClick={() => {
                refreshCart()
              }}
              theme="black"
            >
              {t('cart:CART_ERROR_CALL_TO_ACTION', { count: lineItemsWithErrors.length })}
            </Button>
          </NativeModalFooter>
        </NativeModal>
      )}
    </>
  )
}
